<template>
    <!-- Segmented — reads like a tab strip, behaves like a radio group. The
         value is bound and stored exactly as the other widgets store it; only
         the control changes. -->
    <div
        v-if="widget === 'segmented'"
        class="choice-segmented"
        role="radiogroup"
        :aria-label="field.label"
        @keydown="onKeydown"
    >
        <button
            v-for="(opt, i) in options"
            :key="opt.value"
            type="button"
            role="radio"
            class="choice-segment"
            :class="{ 'is-selected': isSelected(opt.value) }"
            :aria-checked="isSelected(opt.value)"
            :tabindex="isSelected(opt.value) || (!hasSelection && i === 0) ? 0 : -1"
            :disabled="disabled"
            @click="select(opt.value)"
        >
            <b-icon v-if="opt.icon" :icon="opt.icon" size="is-small" />
            <span>{{ opt.label }}</span>
        </button>
    </div>

    <!-- Cards — the same choice with room to breathe, for a decision that
         deserves more than a strip of buttons. -->
    <div
        v-else-if="widget === 'cards'"
        class="choice-cards"
        role="radiogroup"
        :aria-label="field.label"
        @keydown="onKeydown"
    >
        <button
            v-for="(opt, i) in options"
            :key="opt.value"
            type="button"
            role="radio"
            class="choice-card"
            :class="{ 'is-selected': isSelected(opt.value) }"
            :aria-checked="isSelected(opt.value)"
            :tabindex="isSelected(opt.value) || (!hasSelection && i === 0) ? 0 : -1"
            :disabled="disabled"
            @click="select(opt.value)"
        >
            <b-icon v-if="opt.icon" :icon="opt.icon" />
            <span class="choice-card-label">{{ opt.label }}</span>
        </button>
    </div>

    <!-- Dropdown -->
    <b-select
        v-else-if="widget === 'dropdown'"
        :model-value="rawValue"
        @update:model-value="select($event)"
        expanded
        :disabled="disabled"
        v-bind="field.inputProps"
    >
        <option value="">—</option>
        <option v-for="opt in options" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
    </b-select>

    <!-- Switch — booleans only -->
    <b-switch
        v-else-if="widget === 'switch'"
        :model-value="isSelected('1')"
        :disabled="disabled"
        @update:model-value="select($event ? '1' : '0')"
    >
        <field-label :field="field" :editing="!disabled" />
    </b-switch>

    <!-- Checkbox — the shape a boolean has always had -->
    <b-checkbox
        v-else-if="widget === 'checkbox'"
        :model-value="isSelected('1')"
        :disabled="disabled"
        @update:model-value="select($event ? '1' : '0')"
    >
        <field-label :field="field" :editing="!disabled" />
    </b-checkbox>

    <!-- Radios -->
    <div v-else class="choice-radios">
        <b-radio
            v-for="opt in options"
            :key="opt.value"
            :native-value="opt.value"
            :model-value="rawValue"
            @update:model-value="select($event)"
        >
            {{ opt.label }}
        </b-radio>
    </div>
</template>

<script setup>
import { computed } from 'vue';
import { boundChoiceValue, storedChoiceValue } from '@/utils/choiceValue';
import { choiceWidget } from '@/utils/choiceWidget';

/**
 * The control a single-choice field renders with — select, radio or boolean.
 *
 * The widget is presentation: every variant reads and writes the same stored
 * value, so an admin can move a field from radios to a segmented control
 * without touching a single record. `default` keeps whatever the type rendered
 * as before widgets existed.
 */
const props = defineProps({
    field: { type: Object, required: true },
    modelValue: { type: [String, Number, Boolean, null], default: null },
    // Shown but not answerable. The fiche's read mode renders the same control
    // it edits with, so a card does not change shape under the pencil — and a
    // switch that is off looks off, where the word "Non" beside a label is one
    // more thing to read.
    disabled: { type: Boolean, default: false }
});

const emit = defineEmits(['update:modelValue']);

const widget = computed(() => choiceWidget(props.field));

const options = computed(() => {
    if (props.field.type === 'boolean') {
        return [
            { value: '1', label: i18n.t('Yes') },
            { value: '0', label: i18n.t('No') }
        ];
    }

    return (props.field.options || []).map(opt =>
        typeof opt === 'string' ? { value: opt, label: opt } : { value: opt.value, label: opt.label, icon: opt.icon }
    );
});

/** Booleans reach us as 1/0, '1'/'0', true/false and 'true'/'false'. */
function normalise(value) {
    if (value === true || value === 1) return '1';
    if (value === false || value === 0) return '0';
    if (value === 'true') return '1';
    if (value === 'false') return '0';
    if (value === null || value === undefined) return '';

    return String(value);
}

/** What the control binds to — see {@link boundChoiceValue} for the null/'' rule. */
const rawValue = computed(() => {
    if (props.field.type === 'boolean') return normalise(props.modelValue);

    return boundChoiceValue(props.modelValue);
});

const hasSelection = computed(() => options.value.some(o => isSelected(o.value)));

function isSelected(value) {
    return normalise(props.modelValue) === normalise(value);
}

function select(value) {
    // Shown and not offered: a segmented strip or a card row is buttons, which
    // the disabled attribute stops — the keyboard path below does not go
    // through them.
    if (props.disabled) return;

    emit('update:modelValue', storedChoiceValue(value));
}

/**
 * Arrow keys move between choices, which is what a radio group owes anyone not
 * using a mouse — a strip of buttons gets that for free only if we give it.
 */
function onKeydown(event) {
    if (props.disabled) return;

    const keys = ['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp'];
    if (!keys.includes(event.key)) return;

    event.preventDefault();

    const values = options.value.map(o => o.value);
    const current = values.findIndex(v => isSelected(v));
    const step = event.key === 'ArrowRight' || event.key === 'ArrowDown' ? 1 : -1;
    const next = current === -1 ? 0 : (current + step + values.length) % values.length;

    select(values[next]);

    const buttons = event.currentTarget.querySelectorAll('[role="radio"]');
    buttons[next]?.focus();
}
</script>

<style scoped>
.choice-segmented {
    display: inline-flex;
    border: 1px solid var(--bulma-border);
    border-radius: var(--bulma-radius);
    overflow: hidden;
    max-width: 100%;
}

.choice-segment {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.35rem;
    flex: 1 1 auto;
    min-width: 0;
    padding: 0.4rem 0.9rem;
    border: 0;
    border-right: 1px solid var(--bulma-border);
    background: var(--bulma-scheme-main);
    color: var(--bulma-text);
    font-size: 0.9rem;
    cursor: pointer;
    white-space: nowrap;
    transition:
        background-color 0.15s,
        color 0.15s;
}

.choice-segment:last-child {
    border-right: 0;
}

.choice-segment:hover {
    background: hsl(var(--bulma-primary-h) var(--bulma-primary-s) var(--bulma-primary-l) / 0.08);
}

.choice-segment:disabled,
.choice-card:disabled {
    cursor: not-allowed;
    opacity: 0.6;
}

.choice-segment:disabled:hover:not(.is-selected) {
    background: transparent;
}

.choice-segment.is-selected {
    background: var(--bulma-primary);
    color: var(--bulma-primary-invert);
}

.choice-segment:focus-visible {
    outline: 2px solid var(--bulma-link);
    outline-offset: -2px;
}

.choice-cards {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
}

.choice-card {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0.25rem;
    flex: 1 1 8rem;
    min-width: 0;
    padding: 0.75rem 1rem;
    border: 1px solid var(--bulma-border);
    border-radius: var(--bulma-radius);
    background: var(--bulma-scheme-main);
    color: var(--bulma-text);
    cursor: pointer;
    transition:
        border-color 0.15s,
        background-color 0.15s;
}

.choice-card:hover {
    border-color: var(--bulma-primary);
}

.choice-card.is-selected {
    border-color: var(--bulma-primary);
    background: hsl(var(--bulma-primary-h) var(--bulma-primary-s) var(--bulma-primary-l) / 0.1);
    font-weight: 600;
}

.choice-card:focus-visible {
    outline: 2px solid var(--bulma-link);
    outline-offset: 2px;
}

.choice-card-label {
    font-size: 0.9rem;
    text-align: center;
}
</style>
